import { ApiClient } from '../utils/apiClient';
import { registerUserScopedCache } from './dedupedFetch';

export type CostRateSource =
  | 'salary'
  | 'company-job-title-cost'
  | 'company-job-title-charge'
  | 'global-job-title-cost'
  | 'global-job-title-charge'
  | 'none';

/**
 * The terms that turned one salary record into a daily cost rate. Published
 * beside a `salary`-sourced rate, and only to a holder of BOTH
 * `view:rates:cost` and `view:salary` — with the rate beside it, it gives the
 * salary back. `baseValue` is in the record's own `currency` (blank: the
 * workspace currency); the rate beside it is already converted.
 */
export type CostRateDerivation = {
  payPeriod: 'ANNUAL' | 'DAILY' | 'HOURLY';
  /** Decimal as a string. */
  baseValue: string;
  currency: string;
  factor:
    | { kind: 'hours-per-day'; hours: string }
    | { kind: 'annual'; uplift: string; workingDaysInYear: number }
    | { kind: 'daily' };
  /** What base × factor came to: the day rate in the record's currency, before conversion. */
  dayRate: string;
  /** `YYYY-MM-DD` of the record in force. */
  effectiveDate: string;
};

/** One effective-dated cost segment of an allocation window. */
export type ProjectRoiCostSegment = {
  from: string;
  to: string;
  days: number;
  dailyRate: number;
  cost: number;
  costRateDerivation?: CostRateDerivation;
};

/**
 * Machine-readable cause when a row's cost rate is unresolved or clamped
 * (fix-pack AC-8/AC-9) — the most specific cause encountered along the resolver
 * chain. Absent whenever a rate resolved normally. Cost-gated server-side, so a
 * caller without `view:rates:cost` never sees it (nor the cost column it
 * explains).
 */
export type CostRateReason =
  | 'no-salary-window'
  | 'missing-fx-rate'
  | 'clamped-by-hire-date'
  | 'outside-employment'
  | 'no-rate-card';

export type ProjectRoiCostBreakdownItem = {
  allocationId?: string;
  employeeId?: string;
  employeeName?: string;
  role?: string;
  days?: number;
  /** Period-blended day rate (`cost / days`) — an average when the rate varies. */
  costRate?: number;
  costRateSource?: CostRateSource;
  costRateJobTitle?: string;
  cost?: number;
  isRoleSpecific?: boolean;
  /** True when the cost rate changes across the allocation (salary / rota / year). */
  costRateVaries?: boolean;
  /** Effective-dated cost segments backing the blended rate + "varies" hint. */
  costSegments?: ProjectRoiCostSegment[];
  /** Why the rate is unresolved or clamped (AC-8). Absent when one resolved. */
  costRateReason?: CostRateReason;
  /** ISO date the `clamped-by-hire-date` reason names — the employee's earliest start. */
  costRateClampDate?: string;
  /** The salary terms behind `costRate` — the first working segment's. Absent unless salary-sourced and permitted. */
  costRateDerivation?: CostRateDerivation;
};

export type ChargeRateSource =
  | 'rate-card-override'
  | 'rate-card'
  | 'global-default'
  | 'fallback'
  | 'none'
  // values emitted by the backend rate-resolver service
  | 'project-override'
  | 'project-card'
  | 'global-card'
  // legacy backend values from the pre-rate-card code path
  | 'default'
  | 'role_specific'
  | 'job_title';

export type ProjectRoiRevenueBreakdownItem = {
  source?: string;
  amount?: number;
  description?: string;
  calculated?: number;
  cap?: number;
  final?: number;
  /** Per-allocation fields (VARIABLE / CAPPED pricing) */
  allocationId?: string;
  employeeName?: string;
  role?: string;
  days?: number;
  chargeableRate?: number;
  revenue?: number;
  rateSource?: ChargeRateSource;
  rateCardName?: string;
  rateCardId?: string;
  [key: string]: unknown;
};

/**
 * The fields present on **every** ROI payload — the bulk map and the
 * single-project endpoint alike.
 *
 * Split out from {@link ProjectRoiEntry} by APO-24: `GET /api/projects/roi/all`
 * omits `costBreakdown` / `revenueBreakdown` by default (they are per-allocation
 * arrays no bulk consumer reads, and the bulk of a ~245 kB body). Everything
 * here — every scalar, plus `staffingRequirements` and `summary` — is returned
 * in both shapes.
 */
export type ProjectRoiSummaryEntry = {
  projectId: string;
  projectName?: string;
  pricingModel?: 'FIXED' | 'VARIABLE' | 'CAPPED' | string;
  revenue?: number;
  totalCost?: number;
  roi?: number;
  /**
   * Estimate gross margin (%): (revenue − totalCost) ÷ revenue × 100.
   * Backend-computed, and **null when there is no revenue to take a margin on**
   * — a cancelled project, or one whose computed chargeable revenue is zero.
   * The same rule `/project-reports/*` has always applied; render it as "—",
   * never as 0, which would assert a break-even that did not happen.
   */
  margin?: number | null;
  currentValue?: number;
  currency?: string;
  /**
   * Remaining budget after all projected charges (chargeable rates).
   * Null for VARIABLE projects (no fixed cap). Negative means over-committed.
   * Requires `view:project:value` — stripped by the API for users without it.
   */
  forecastRemaining?: number | null;

  // ── Actuals-hardened (Feature 15) ───────────────────────────────────────────
  // Cost AND charge refined by real logged time (coverage-weighted blend). The
  // estimate fields above (`totalCost` / `roi` / `margin`) are retained unchanged
  // so surfaces can show estimate-vs-hardened side by side. Gating mirrors the
  // backend `ROI_SENSITIVE_FIELDS`: the cost-derived fields below are stripped
  // for a holder without `view:rates:cost` (hence optional); the charge / margin
  // / ROI / coverage fields ride the endpoint's `view:project:margin` gate.
  /** Hardened forecast cost (actual where logged + planned estimate for the rest). Cost-gated. */
  effectiveCost?: number;
  /** Raw actual logged cost to date. Cost-gated. */
  actualCost?: number;
  /** Hardened profit/(loss): hardened revenue − effectiveCost. Cost-gated. */
  hardenedProfitLoss?: number;
  /** Real burn: actualCost ÷ planned-to-date cost (%). Null when no elapsed plan. Cost-gated. */
  burnPct?: number | null;
  /** Hardened forecast chargeable value. Rides the margin gate. */
  effectiveCharge?: number;
  /** Raw actual chargeable value of logged hours. Rides the margin gate. */
  actualCharge?: number;
  /** Gross margin on the hardened cost (%). Null on no hardened revenue, like {@link margin}. Rides the margin gate. */
  marginHardened?: number | null;
  /** Return on the hardened cost (%). Rides the margin gate. */
  roiHardened?: number;
  /** Logged ÷ planned-to-date hours (%). Operational; null when no elapsed plan. */
  coveragePct?: number | null;
  /**
   * Total logged hours behind the hardened figures (fix-pack AC-35). Rides the
   * margin gate like `coveragePct`, and is read WITH it: `coveragePct === null ∧
   * loggedHours > 0` is a project whose cost is entirely timesheet-derived
   * because it has no allocations to plan against. Optional because a bulk row
   * served from a snapshot written before this field existed will not carry it.
   */
  loggedHours?: number;

  /**
   * Legacy per-role rollup, derived from `costBreakdown` server-side. Retained
   * in the trimmed bulk response: `ProjectRoiDataGapBadge` reads `role` +
   * `dailyRate` from it to flag unrated roles in the Projects list.
   */
  staffingRequirements?: Array<{
    role?: string;
    dailyRate?: number;
    totalCost?: number;
    days?: number;
  }>;
  summary?: {
    roiFormatted?: string;
    profitable?: boolean;
    profitLoss?: number;
    averageCostRate?: number;
    averageChargeableRate?: number;
    roleSpecificAllocations?: number;
    generalAllocations?: number;
  };
  [key: string]: unknown;
};

/**
 * The **full** ROI payload: everything in {@link ProjectRoiSummaryEntry} plus
 * the two per-allocation breakdown arrays.
 *
 * Returned by `GET /api/projects/:id/roi` (always — see {@link
 * ProjectRoiService.getProjectRoi}) and by `GET /api/projects/roi/all` **only**
 * when called with `?include=breakdown`, which nothing in this app does. Reach
 * for the single-project endpoint when you need a breakdown; every detail panel
 * already does.
 */
export type ProjectRoiEntry = ProjectRoiSummaryEntry & {
  costBreakdown?: ProjectRoiCostBreakdownItem[];
  revenueBreakdown?: ProjectRoiRevenueBreakdownItem[];
};

/**
 * The bulk map from `GET /api/projects/roi/all`. Typed as the **summary** entry
 * because the default response omits the breakdown arrays (APO-24 / AC-20).
 */
export type ProjectRoiMap = Record<string, ProjectRoiSummaryEntry>;

/**
 * Small caching layer for the `/api/projects/roi/all` endpoint.
 *
 * The Projects page and the Reports page both call this endpoint on mount
 * (and sometimes multiple hooks within the same page trigger it during the
 * same render cycle). Without coordination we would issue several identical
 * requests. This service does two things:
 *
 *   1. Keeps a short-lived in-memory cache (60s) so quickly re-visiting the
 *      page doesn't refetch. Callers can call {@link clearCache} after
 *      mutations that would change ROI (e.g. saving allocations).
 *   2. De-duplicates overlapping in-flight fetches via a shared promise, so
 *      concurrent callers share a single HTTP request.
 */
class ProjectRoiService {
  private cache: ProjectRoiMap | null = null;
  private cacheTimestamp = 0;
  private readonly CACHE_TTL = 60 * 1000;
  private inFlight: Promise<ProjectRoiMap> | null = null;
  /** Bumped by `clearCache()`, so a request in flight then cannot refill the cache. */
  private generation = 0;
  /** Per-project in-flight single-project requests — see {@link getProjectRoi}. */
  private readonly inFlightByProject = new Map<string, Promise<ProjectRoiEntry>>();

  /**
   * Fetches the ROI map for all projects.
   *
   * Returns an empty map immediately when `userPermissions` is provided
   * and lacks `view:project:margin`. Callers should pass the array from
   * `usePermissions()` so we don't fire a request that will 403.
   *
   * Deliberately sends **no** `?include=breakdown` (APO-24): every entry is a
   * {@link ProjectRoiSummaryEntry}, without the per-allocation breakdown
   * arrays. Nothing reading this map needs them — the panels that do call
   * {@link getProjectRoi} for the one project they are showing. Adding the
   * parameter here would restore ~85% of the response body for no consumer.
   */
  async getAllProjectRois(
    apiClient: ApiClient,
    userPermissions?: string[],
  ): Promise<ProjectRoiMap> {
    if (
      userPermissions !== undefined &&
      !userPermissions.includes('view:project:margin')
    ) {
      return {};
    }

    if (this.cache && Date.now() - this.cacheTimestamp < this.CACHE_TTL) {
      return this.cache;
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    const generation = this.generation;
    this.inFlight = (async () => {
      try {
        const data = (await apiClient.get<ProjectRoiMap>('/api/projects/roi/all')) ?? {};
        if (generation === this.generation) {
          this.cache = data;
          this.cacheTimestamp = Date.now();
        }
        return data;
      } finally {
        if (generation === this.generation) this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  clearCache(): void {
    this.generation += 1;
    this.cache = null;
    this.cacheTimestamp = 0;
    this.inFlight = null;
    // Nothing single-project is cached, but an in-flight request started before
    // a mutation would return pre-mutation figures to anyone who joined it.
    this.inFlightByProject.clear();
  }

  /**
   * Fetch the **full** ROI payload for a single project, breakdown arrays
   * included — this endpoint is unchanged by APO-24 and is the supported way to
   * get a per-allocation breakdown. Used by the Edit Project
   * dialog's diagnostic panel where we want per-allocation data and the bulk
   * endpoint may not be loaded yet.
   *
   * **In-flight de-duplication, not caching.** Several panels on one screen ask
   * for the same project's ROI as they mount (the Money tab's summary band
   * and its Cost & ROI breakdown both do), which issued two identical requests
   * for one page load. Concurrent callers now share one promise. Deliberately no
   * TTL cache: a later call — a Refresh click, a re-mount after saving an
   * allocation — starts after the first has settled and so still goes to the
   * server, which is what those callers are asking for.
   */
  async getProjectRoi(apiClient: ApiClient, projectId: string): Promise<ProjectRoiEntry> {
    const pending = this.inFlightByProject.get(projectId);
    if (pending) return pending;

    const request = apiClient
      .get<ProjectRoiEntry>(`/api/projects/${projectId}/roi`)
      .finally(() => {
        // Cleared on both success and failure, so a failed load never wedges
        // every later caller onto the same rejected promise.
        this.inFlightByProject.delete(projectId);
      });
    this.inFlightByProject.set(projectId, request);
    return request;
  }
}

export const projectRoiService = new ProjectRoiService();
registerUserScopedCache({ clear: () => projectRoiService.clearCache() });
